<template>
  <ActivityHistoryTable
    :title="text.history.title"
    :subtitle="text.history.subtitle"
    :loading="loading"
    :rows="rows"
    :loading-text="text.history.loading"
    :empty-text="text.history.empty"
    :headers="text.history.table.headers"
    :row-fallback="text.history.fallback"
  />
</template>

<script>
import ActivityHistoryTable from '@/components/common/ActivityHistoryTable.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import financialAccountsService from '@/services/financialAccounts';
import { extractCollection } from '@/utils/dataNormalization';
import { formatDateTimeShort } from '@/utils/datePresentation';

const { catalog: domainText } = useDomainText('financial');

export default {
  name: 'FinancialAccountDetailHistoryTab',
  components: {
    ActivityHistoryTable,
  },
  props: {
    financialAccountId: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      text: domainText.detailAccount,
      loading: false,
      rows: [],
      requestToken: 0,
    };
  },
  computed: {
    resolvedFinancialAccountId() {
      return Number(this.financialAccountId || 0);
    },
  },
  watch: {
    resolvedFinancialAccountId: {
      immediate: true,
      handler() {
        this.loadHistory();
      },
    },
  },
  methods: {
    async loadHistory() {
      const financialAccountId = Number(this.resolvedFinancialAccountId || 0);
      if (financialAccountId <= 0) {
        this.rows = [];
        return;
      }

      this.loading = true;
      const token = this.requestToken + 1;
      this.requestToken = token;

      try {
        const response = await financialAccountsService.activity(financialAccountId, { per_page: 200 });
        if (token !== this.requestToken) {
          return;
        }

        this.rows = extractCollection(response)
          .map((row) => this.mapHistoryRow(row))
          .filter((row) => row !== null);
      } catch (error) {
        if (token === this.requestToken) {
          this.rows = [];
        }
      } finally {
        if (token === this.requestToken) {
          this.loading = false;
        }
      }
    },
    mapHistoryRow(raw) {
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const moduleCode = String(raw.module || '').trim().toLowerCase();
      const eventType = String(raw.event_type || '').trim().toLowerCase();
      const dateSource = raw.happened_at || raw.created_at || raw.date_label;

      return {
        id: Number(raw.id || 0) || `${moduleCode}-${eventType}-${Date.now()}`,
        date_label: formatDateTimeShort(dateSource, { fallback: this.text.history.fallback.date }),
        module_label: String(raw.module_label || '').trim() || this.toTitle(moduleCode) || this.text.history.fallback.module,
        event_label: String(raw.event_label || '').trim() || this.toTitle(eventType) || this.text.history.fallback.event,
        description_label: String(raw.description_label || '').trim()
          || String(raw.description || raw.title || this.text.history.fallback.description).trim(),
        actor_label: String(raw.actor_label || raw.actor_name || this.text.history.fallback.actor).trim(),
      };
    },
    toTitle(value) {
      const normalized = String(value || '').trim().replace(/[_-]+/g, ' ');
      if (normalized === '') {
        return '';
      }

      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    },
  },
};
</script>
